import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtNOK, fmtKwh, fmtNumber, fmtDate, contractTypeLabel } from "@/lib/format";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Zap, TrendingUp, FileText, Receipt, Gauge, AlertCircle } from "lucide-react";
import { SpotPriceCard } from "@/components/SpotPriceCard";

const chartAxis = { stroke: "#3f3f46", fontSize: 10, tickLine: false, axisLine: false };
const chartTooltip = {
  contentStyle: { background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" }, itemStyle: { color: "#fafafa" },
};

export default function PortalDashboard() {
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [consumption, setConsumption] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/portal/me"),
      api.get("/portal/summary"),
      api.get("/portal/consumption?days=90"),
      api.get("/portal/contracts"),
      api.get("/portal/invoices"),
    ]).then(([m, s, c, ct, i]) => {
      setMe(m.data); setSummary(s.data); setConsumption(c.data); setContracts(ct.data); setInvoices(i.data);
    }).catch(() => {});
  }, []);

  if (!me) return <Spinner />;

  return (
    <div data-testid="portal-dashboard">
      <PageHeader title={`Velkommen, ${me.customer?.full_name || "kunde"}`} subtitle={`Kundenr ${me.customer?.customer_number}`} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Gauge} label="Målepunkter" value={summary?.metering_points || 0} />
        <KpiCard icon={FileText} label="Aktive avtaler" value={summary?.active_contracts || 0} />
        <KpiCard icon={Zap} label="Forbruk hittil" value={fmtKwh(summary?.ytd_consumption_kwh || 0)} accent="text-amber-400" />
        <KpiCard icon={AlertCircle} label="Utestående" value={fmtNOK(summary?.outstanding_nok || 0)} accent="text-orange-400" />
      </div>

      <SpotPriceCard />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Card className="xl:col-span-2 p-5" testid="portal-consumption-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Forbruk</div>
              <div className="font-heading text-xl text-zinc-50 mt-0.5">Siste 90 dager</div>
            </div>
            <div className="text-2xl font-heading tabular-nums">{fmtKwh(summary?.ytd_consumption_kwh || 0)}</div>
          </div>
          {consumption.length === 0 ? <Empty title="Ingen forbruksdata ennå" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={consumption}>
                <defs>
                  <linearGradient id="pcons" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#141414" vertical={false} />
                <XAxis dataKey="date" {...chartAxis} />
                <YAxis {...chartAxis} />
                <Tooltip {...chartTooltip} formatter={(v) => [fmtKwh(v), ""]} />
                <Area type="monotone" dataKey="kwh" stroke="#f59e0b" strokeWidth={1.75} fill="url(#pcons)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-3">Aktive avtaler</div>
          {contracts.filter(c => c.status === "aktiv").length === 0 ? <Empty title="Ingen aktive avtaler" /> : (
            <div className="space-y-3">
              {contracts.filter(c => c.status === "aktiv").slice(0, 5).map((c) => (
                <div key={c.id} className="border-b border-zinc-900 pb-3 last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs text-emerald-400">{c.contract_number}</span>
                    <StatusBadge value={c.status} />
                  </div>
                  <div className="text-zinc-100 text-sm mt-1">{contractTypeLabel(c.type)}</div>
                  <div className="text-[11px] text-zinc-500">Fra {fmtDate(c.start_date)} · påslag {c.markup_ore_per_kwh?.toFixed(2)} øre/kWh</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden" testid="portal-invoices-list">
        <div className="p-4 border-b border-zinc-900 font-heading font-medium flex justify-between items-center">
          <span>Siste fakturaer</span>
          <Link to="/portal/fakturaer" className="text-xs text-emerald-400 hover:underline">Se alle</Link>
        </div>
        {invoices.length === 0 ? <Empty title="Ingen fakturaer" /> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">Fakturanr</th>
                <th className="py-2 px-4 font-medium">Dato</th>
                <th className="py-2 px-4 font-medium">Forfall</th>
                <th className="py-2 px-4 font-medium">Forbruk</th>
                <th className="py-2 px-4 font-medium text-right">Total</th>
                <th className="py-2 px-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 8).map((i) => (
                <tr key={i.id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                  <td className="py-2 px-4"><Link to={`/portal/fakturaer/${i.id}`} className="font-mono text-xs text-emerald-400">{i.invoice_number}</Link></td>
                  <td className="py-2 px-4 text-zinc-400">{fmtDate(i.issue_date)}</td>
                  <td className="py-2 px-4 text-zinc-400">{fmtDate(i.due_date)}</td>
                  <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtKwh(i.consumption_kwh)}</td>
                  <td className="py-2 px-4 tabular-nums text-zinc-100 text-right">{fmtNOK(i.total_nok)}</td>
                  <td className="py-2 px-4"><StatusBadge value={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
        <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />
      </div>
      <div className="font-heading text-3xl tabular-nums">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}
